import { z } from 'zod';

// D2.2.3 — Per-step Zod schemas for the intake portal (10 steps).
//
// Each step submission is validated against its own schema via
// `.safeParse()` at the server boundary (see
// `packages/core/tenant/intake-submission.ts` for the submission
// service, and `apps/server/src/routes/intake-portal.ts` for the
// route). Per the design doc §7, a tenant drafts a step at a time,
// autosaves partial state, and only the review/submit step assembles
// the full `TenantKit`. Accordingly these schemas gate per-step
// internal consistency only — the final kit assembly happens at
// D3.3 commit time where `tenantKitSchema` runs end-to-end.
//
// Source of truth: `docs/TENANT-INTAKE-PORTAL-DESIGN-2026-04-21.md`
// §7.1 – §7.10. Where the design introduced fields that overlap with
// `packages/config/src/brand-kit.ts` or `src/tenant-config.ts`, we
// mirror the *shape* defined there rather than re-stating it — but
// because the intake portal captures draft state (e.g. blob upload
// refs not yet promoted to canonical URIs), the schemas are kept
// standalone here so the "staged intake data" vs. "committed tenant
// kit" distinction stays clean.
//
// Step keys (`'1' | '2' | … | '10'`) are canonical; the numeric
// `lastStep` column on `TenantIntake` (1–10) is a convenience cursor
// for the UI to resume at the right step.

// ─── Canonical step identifiers ──────────────────────────────────────

export const INTAKE_STEP_IDS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
] as const;

export type IntakeStepId = (typeof INTAKE_STEP_IDS)[number];

export function isIntakeStepId(value: unknown): value is IntakeStepId {
  return (
    typeof value === 'string' &&
    (INTAKE_STEP_IDS as readonly string[]).includes(value)
  );
}

export const intakeStepIdSchema = z.enum(INTAKE_STEP_IDS);

// ─── Shared primitives ───────────────────────────────────────────────

/** 3–40 chars kebab-case per design §7.1. */
export const intakeSlugRegex = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/;

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a 6-digit hex like #62A2C3');

const phoneUsRegex = /^\+?1?\d{10}$/;
const einRegex = /^\d{2}-\d{7}$/;
const hhmm = z.string().regex(/^\d{2}:\d{2}$/, 'Time must be HH:MM (24-hour)');
const iso8601Date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');

export const intakeAddressSchema = z.object({
  line1: z.string().min(1).max(120),
  line2: z.string().max(120).optional(),
  city: z.string().min(1).max(80),
  state: z.string().length(2),
  postalCode: z.string().regex(/^\d{5}(-\d{4})?$/),
  country: z.literal('US'),
});

const blobUploadRefSchema = z.object({
  blobKey: z.string().min(1),
  publicUrl: z.string().url(),
  mimeType: z.string().min(1),
  sizeBytes: z.number().int().positive(),
});

// Curated font list matches the portal's v1 picker (design §7.2).
// Custom font upload is a Phase-2 field handled by admin-only tools;
// the portal v1 does not accept arbitrary font URLs from prospects.
export const INTAKE_CURATED_FONTS = [
  'Inter',
  'Manrope',
  'Open Sans',
  'Lato',
  'Nunito',
  'Playfair Display',
  'Fraunces',
  'Cormorant Garamond',
  'Poppins',
  'DM Sans',
  'Work Sans',
  'Montserrat',
  'Merriweather',
  'Source Serif Pro',
] as const;

const curatedFontSchema = z.enum(INTAKE_CURATED_FONTS);

// Closed v1 role enum per RBAC-DESIGN-2026-04-21.md.
export const INTAKE_ROLE_SLUGS = [
  'owner',
  'manager',
  'receptionist',
  'walker',
  'groomer',
] as const;

const intakeRoleSlugSchema = z.enum(INTAKE_ROLE_SLUGS);

// Closed v1 vertical (design §7.1); additional verticals land
// additively.
const intakeVerticalSchema = z.literal('pet-services');

const intakeTimezoneSchema = z.enum([
  'America/Denver',
  'America/Los_Angeles',
  'America/Chicago',
  'America/New_York',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
]);

const intakeEntityTypeSchema = z.enum([
  'LLC',
  'S-Corp',
  'C-Corp',
  'Sole Proprietor',
  'Partnership',
  'Other',
]);

// 14-module v1 universe per design §7.8. Mirrors the
// `CANONICAL_MODULE_SLUGS` list in packages/core/modules/registry.ts —
// keeping a copy here avoids a cross-package import in the intake
// schema file (and the registry's authoritative list is the one the
// D1.4.1 dependency validator runs against).
export const INTAKE_V1_MODULE_SLUGS = [
  'tenant-config',
  'auth',
  'rbac',
  'crm',
  'messaging',
  'booking',
  'vaccination',
  'payments',
  'loyalty',
  'staff-scheduling',
  'staff-payroll',
  'incident-reporting',
  'walking',
  'ai-manager',
  'ai-monitor',
] as const;

const intakeModuleSlugSchema = z.enum(INTAKE_V1_MODULE_SLUGS);

const INTAKE_FOUNDATION_MODULES: readonly string[] = [
  'tenant-config',
  'auth',
  'rbac',
  'crm',
  'messaging',
];

// ─── Step 1: Identity & business profile ─────────────────────────────

export const intakeStep1Schema = z.object({
  legalName: z.string().min(2).max(200),
  displayName: z.string().min(2).max(80),
  slug: z.string().regex(intakeSlugRegex),
  slugAvailabilityChecked: z.literal(true),
  vertical: intakeVerticalSchema,
  timezone: intakeTimezoneSchema,
  primaryAddress: intakeAddressSchema,
  primaryPhone: z.string().regex(phoneUsRegex),
  websiteUrl: z.string().url().optional(),
  ein: z.string().regex(einRegex).optional(),
  entityType: intakeEntityTypeSchema,
  billingContactEmail: z.string().email(),
  billingContactName: z.string().min(2).max(120),
});
export type IntakeStep1Payload = z.infer<typeof intakeStep1Schema>;

// ─── Step 2: Brand kit upload ────────────────────────────────────────

export const intakeStep2Schema = z.object({
  logo: z.object({
    primarySvg: blobUploadRefSchema,
    primaryPngLight: blobUploadRefSchema,
    primaryPngDark: blobUploadRefSchema.optional(),
    favicon: blobUploadRefSchema,
    emailHeader: blobUploadRefSchema.optional(),
  }),
  colors: z.object({
    primary: hexColor,
    secondary: hexColor,
    accent: hexColor,
    background: hexColor.default('#FFFFFF'),
    text: hexColor.default('#1A1A1A'),
  }),
  typography: z.object({
    primaryFontFamily: curatedFontSchema,
    headingFontFamily: curatedFontSchema,
  }),
  copy: z.object({
    companyName: z.string().min(2).max(80),
    tagline: z.string().max(140).optional(),
    supportEmail: z.string().email(),
    supportPhone: z.string().regex(phoneUsRegex).optional(),
  }),
  terminology: z
    .record(z.string(), z.string())
    .default({})
    .refine((obj) => Object.keys(obj).length <= 20, {
      message: 'At most 20 terminology overrides',
    }),
});
export type IntakeStep2Payload = z.infer<typeof intakeStep2Schema>;

// ─── Step 3: Stripe Connect kickoff ──────────────────────────────────

export const intakeStep3Schema = z.object({
  stripeAccountId: z.string().regex(/^acct_[A-Za-z0-9]+$/),
  accountLinkGeneratedAt: z.string().datetime(),
  accountLinkLastClickedAt: z.string().datetime().optional(),
  onboardingCompleteAt: z.string().datetime().optional(),
  capabilities: z.object({
    cardPayments: z.enum(['active', 'pending', 'inactive']),
    transfers: z.enum(['active', 'pending', 'inactive']),
    achPayments: z.enum(['active', 'pending', 'inactive']).optional(),
  }),
  requirementsCurrentlyDue: z.array(z.string()).default([]),
  requirementsEventuallyDue: z.array(z.string()).default([]),
  payoutsEnabled: z.boolean(),
  chargesEnabled: z.boolean(),
  statementDescriptor: z
    .string()
    .min(5)
    .max(22)
    .regex(/^[A-Za-z0-9 ]+$/),
  tenantConfirmedMOR: z.literal(true),
});
export type IntakeStep3Payload = z.infer<typeof intakeStep3Schema>;

// ─── Step 4: Payments & accounting integrations ──────────────────────

const achSchema = z
  .object({
    desired: z.boolean(),
    bankName: z.string().optional(),
    contactName: z.string().optional(),
    contactEmail: z.string().email().optional(),
    contactPhone: z.string().regex(phoneUsRegex).optional(),
    _note: z.string().max(500).optional(),
  })
  .refine(
    (v) =>
      !v.desired ||
      (!!v.bankName && !!v.contactName && (!!v.contactEmail || !!v.contactPhone)),
    {
      message:
        'If ACH is desired, bank name + contact name + (email or phone) are required',
    },
  );

export const intakeStep4Schema = z.object({
  quickBooks: z.discriminatedUnion('variant', [
    z.object({
      variant: z.literal('qbo'),
      companyId: z.string().optional(),
      authorizedAt: z.string().datetime().optional(),
      syncScopes: z
        .array(z.enum(['invoices', 'payments', 'bank_deposits', 'products']))
        .default([]),
      accountantEmail: z.string().email().optional(),
    }),
    z.object({
      variant: z.literal('qbd'),
      accountantEmail: z.string().email(),
      accountantName: z.string().min(2).max(120),
      exportCadence: z.enum(['weekly', 'biweekly', 'monthly']),
      _comment: z.string().optional(),
    }),
    z.object({
      variant: z.literal('none'),
      confirmedNoAccountingSync: z.literal(true),
    }),
  ]),
  ach: achSchema,
  paymentChannels: z.object({
    online: z.boolean().default(true),
    inPerson: z.boolean().default(false),
    terminalModel: z
      .enum(['bbpos_wisepos_e', 'stripe_reader_s700', 'tbd'])
      .optional(),
    manualPhone: z.boolean().default(false),
  }),
});
export type IntakeStep4Payload = z.infer<typeof intakeStep4Schema>;

// ─── Step 5: Staff roster ────────────────────────────────────────────

export const intakeStaffEntrySchema = z.object({
  firstName: z.string().min(1).max(60),
  lastName: z.string().min(1).max(60),
  email: z.string().email(),
  phone: z.string().regex(phoneUsRegex).optional(),
  role: intakeRoleSlugSchema,
  sendInviteOnSubmit: z.boolean().default(true),
  notes: z.string().max(280).optional(),
});

export const intakeStep5Schema = z.object({
  staff: z
    .array(intakeStaffEntrySchema)
    .min(1, { message: 'At least one staff member required' })
    .max(200)
    .refine(
      (arr) => new Set(arr.map((s) => s.email.toLowerCase())).size === arr.length,
      { message: 'Duplicate email address in staff roster' },
    )
    .refine((arr) => arr.some((s) => s.role === 'owner'), {
      message: 'At least one Owner required',
    }),
  csvImportSource: z.string().optional(),
});
export type IntakeStep5Payload = z.infer<typeof intakeStep5Schema>;

// ─── Step 6: Domain preference ───────────────────────────────────────

const cnameHostnameSchema = z
  .string()
  .regex(/^[a-z0-9.-]+\.[a-z]{2,}$/);

export const intakeStep6Schema = z.object({
  primarySubdomain: z.string().regex(intakeSlugRegex),
  confirmedSubdomain: z.literal(true),
  cnameIntent: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('none'),
      _ack: z.enum(['not_interested', 'not_sure_yet']),
    }),
    z.object({
      kind: z.literal('later'),
      desiredHostnameCustomer: cnameHostnameSchema.optional(),
      desiredHostnameAdmin: cnameHostnameSchema.optional(),
      ownsDomain: z.boolean(),
      _ack: z.literal('phase_2_understood'),
    }),
  ]),
});
export type IntakeStep6Payload = z.infer<typeof intakeStep6Schema>;

// ─── Step 7: Service catalog seed ────────────────────────────────────

const dayHoursSchema = z
  .object({
    isOpen: z.boolean(),
    open: hhmm.optional(),
    close: hhmm.optional(),
  })
  .refine((v) => !v.isOpen || (!!v.open && !!v.close), {
    message: 'Open/close required when day is open',
  })
  .refine((v) => !v.isOpen || v.open! < v.close!, {
    message: 'Close must be after open',
  });

export const intakeServiceSeedSchema = z.object({
  slug: z
    .string()
    .regex(/^[a-z0-9-]+$/)
    .min(2)
    .max(40),
  displayName: z.string().min(2).max(80),
  type: z.enum([
    'daycare',
    'boarding',
    'grooming',
    'walking',
    'training',
    'retail',
    'other',
  ]),
  priceCents: z.number().int().nonnegative(),
  durationMinutes: z.number().int().positive().nullable(),
  applicableStaffRoles: z.array(intakeRoleSlugSchema).default([]),
  requiresVaccinationCheck: z.boolean().default(true),
  minLeadTimeMinutes: z.number().int().nonnegative().default(0),
  maxAdvanceDays: z.number().int().positive().default(90),
});

export const intakeStep7Schema = z.object({
  businessHours: z.object({
    monday: dayHoursSchema,
    tuesday: dayHoursSchema,
    wednesday: dayHoursSchema,
    thursday: dayHoursSchema,
    friday: dayHoursSchema,
    saturday: dayHoursSchema,
    sunday: dayHoursSchema,
  }),
  holidays: z
    .array(
      z.object({
        date: iso8601Date,
        label: z.string().min(1).max(60),
        closed: z.boolean().default(true),
      }),
    )
    .default([]),
  services: z
    .array(intakeServiceSeedSchema)
    .min(1, { message: 'At least one service required' })
    .max(200)
    .refine(
      (arr) => new Set(arr.map((s) => s.slug)).size === arr.length,
      { message: 'Duplicate service slug' },
    ),
  capacityRules: z.object({
    dailyDaycareCap: z.number().int().positive().optional(),
    overnightBoardingCap: z.number().int().positive().optional(),
    concurrentGroomingSlots: z.number().int().positive().optional(),
    concurrentWalksPerWalker: z.number().int().positive().default(3),
  }),
  terminologyOverrides: z.record(z.string(), z.string()).default({}),
});
export type IntakeStep7Payload = z.infer<typeof intakeStep7Schema>;

// ─── Step 8: Module activation ───────────────────────────────────────

export const intakeStep8Schema = z.object({
  modules: z
    .array(intakeModuleSlugSchema)
    .min(5)
    .refine(
      (arr) => INTAKE_FOUNDATION_MODULES.every((m) => (arr as string[]).includes(m)),
      { message: 'Foundation modules are required' },
    )
    .refine((arr) => new Set(arr).size === arr.length, {
      message: 'Duplicate module',
    }),
  dependencyValidated: z.literal(true),
  tenantRationale: z.record(z.string(), z.string()).default({}),
});
export type IntakeStep8Payload = z.infer<typeof intakeStep8Schema>;

// ─── Step 9: Data migration ──────────────────────────────────────────

const sanitizationRuleSchema = z.object({
  pattern: z.string().min(1).max(120),
  replacement: z.string().max(120),
  scope: z.enum(['all', 'imported_data_and_ui', 'customer_ui']).default('all'),
});

const csvBlobRefSchema = z.object({
  kind: z.enum(['customers', 'pets', 'bookings', 'invoices']),
  blobKey: z.string().min(1),
  sizeBytes: z.number().int().positive(),
});

const csvBlobRefStaffSchema = z.object({
  kind: z.enum(['customers', 'pets', 'bookings', 'invoices', 'staff']),
  blobKey: z.string().min(1),
  sizeBytes: z.number().int().positive(),
});

export const intakeStep9Schema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('gingr'),
    gingrApiKey: z.string().min(10),
    gingrSubdomain: z.string().regex(/^[a-z0-9-]+$/),
    recordCountsConfirmed: z.object({
      customers: z.number().int().nonnegative(),
      pets: z.number().int().nonnegative().optional(),
      invoices: z.number().int().nonnegative().optional(),
      bookings: z.number().int().nonnegative().optional(),
    }),
    loyaltyBackfill: z.enum([
      'retro_current_rate',
      'from_signup_date',
      'none',
    ]),
    sanitizationRules: z.array(sanitizationRuleSchema).default([]),
  }),
  z.object({
    source: z.literal('moego'),
    csvBlobRefs: z.array(csvBlobRefSchema).min(1),
    sanitizationRules: z.array(sanitizationRuleSchema).default([]),
  }),
  z.object({
    source: z.literal('pawloyalty'),
    csvBlobRefs: z.array(csvBlobRefSchema).min(1),
    sanitizationRules: z.array(sanitizationRuleSchema).default([]),
  }),
  z.object({
    source: z.literal('csv'),
    csvBlobRefs: z.array(csvBlobRefStaffSchema).min(1),
    columnMapping: z.record(z.string(), z.string()).optional(),
    sanitizationRules: z.array(sanitizationRuleSchema).default([]),
  }),
  z.object({
    source: z.literal('none'),
    confirmedNoMigration: z.literal(true),
  }),
]);
export type IntakeStep9Payload = z.infer<typeof intakeStep9Schema>;

// ─── Step 10: Review & submit ────────────────────────────────────────

export const intakeStep10Schema = z.object({
  reviewedAllSteps: z.literal(true),
  acknowledgedSoftBlockers: z
    .array(
      z.object({
        stepNumber: z.number().int().min(1).max(9),
        blockerId: z.string().min(1),
        userAck: z.literal(true),
      }),
    )
    .default([]),
  renderedKitHash: z.string().length(64),
  ownerSignature: z.object({
    printedName: z.string().min(2).max(120),
    email: z.string().email(),
    timestamp: z.string().datetime(),
    ipAddress: z.string().optional(),
  }),
  moneyBackGuaranteeAcknowledged: z.literal(true),
  tosAcknowledged: z.literal(true),
  privacyAcknowledged: z.literal(true),
});
export type IntakeStep10Payload = z.infer<typeof intakeStep10Schema>;

// ─── Step-schema registry ────────────────────────────────────────────

/**
 * Map of step id → Zod schema. The submission service indexes into
 * this by the caller's `stepId` to pick the right validator. Using a
 * `Record` with `z.ZodType` keeps the lookup type-safe without forcing
 * callers to narrow the step id at the call site.
 */
export const intakeStepSchemas: Record<IntakeStepId, z.ZodType> = {
  '1': intakeStep1Schema,
  '2': intakeStep2Schema,
  '3': intakeStep3Schema,
  '4': intakeStep4Schema,
  '5': intakeStep5Schema,
  '6': intakeStep6Schema,
  '7': intakeStep7Schema,
  '8': intakeStep8Schema,
  '9': intakeStep9Schema,
  '10': intakeStep10Schema,
};

/** Step id → numeric `lastStep` cursor (1–10). */
export function stepNumberFromId(stepId: IntakeStepId): number {
  return Number(stepId);
}

/** Numeric cursor → canonical step id; null for out-of-range values. */
export function stepIdFromNumber(n: number): IntakeStepId | null {
  if (!Number.isInteger(n) || n < 1 || n > 10) return null;
  return String(n) as IntakeStepId;
}
